import { handleApiError } from '@/shared/utils/errorUtils';
import { tokenStorage } from '@/shared/utils/tokenStorage';

import type {
  ITestAccuracyDailyParams,
  ITestAccuracyOrgsResponse,
  ITestAccuracyProjectsResponse,
  ITestAccuracyStreamDay,
  ITestAccuracyStreamDone,
  ITestAccuracyStreamError,
  ITestAccuracyStreamHandlers,
  ITestAccuracyStreamMeta,
  ITestAccuracyStreamSummary,
  TestAccuracyStreamEvent,
} from '../types/testAccuracy';

const BASE = '/api/ai-automation-admin/test-accuracy';
const API_BASE_URL = (import.meta.env.VITE_BASE_URL || '').replace(/\/$/, '');

function buildUrl(path: string, params?: Record<string, string>): string {
  const search = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        search.set(key, value);
      }
    });
  }
  const qs = search.toString();
  return `${API_BASE_URL}${path}${qs ? `?${qs}` : ''}`;
}

function authHeaders(json = false): HeadersInit {
  const token = tokenStorage.getAccessToken();
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function clearSessionAndRedirectToLogin() {
  tokenStorage.clearAllTokens();
  try {
    localStorage.clear();
  } catch {
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
  }
  window.location.href = '/login';
}

function handleAuthFailureIfNeeded(res: Response): boolean {
  if (res.status !== 401 && res.status !== 403) {
    return false;
  }
  clearSessionAndRedirectToLogin();
  return true;
}

async function parseJsonResponse<T>(res: Response): Promise<T> {
  if (handleAuthFailureIfNeeded(res)) {
    throw new Error('Session expired. Please login again.');
  }
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    throw new Error(data.error || res.statusText || 'Request failed');
  }
  return data;
}

function dispatchStreamEvent(evt: TestAccuracyStreamEvent, handlers: ITestAccuracyStreamHandlers) {
  if (evt.type === 'meta' && handlers.meta) {
    handlers.meta(evt as ITestAccuracyStreamMeta);
    return;
  }
  if (evt.type === 'summary' && handlers.summary) {
    handlers.summary(evt as ITestAccuracyStreamSummary);
    return;
  }
  if (evt.type === 'day' && handlers.day) {
    handlers.day(evt as ITestAccuracyStreamDay);
    return;
  }
  if (evt.type === 'error') {
    const errEvt = evt as ITestAccuracyStreamError;
    if (handlers.error) {
      handlers.error(errEvt);
    }
    throw new Error(errEvt.message || 'Stream error');
  }
  if (evt.type === 'done' && handlers.done) {
    handlers.done(evt as ITestAccuracyStreamDone);
  }
}

export const testAccuracyService = {
  getOrgs: async (): Promise<ITestAccuracyOrgsResponse> => {
    try {
      const res = await fetch(buildUrl(`${BASE}/orgs/`), { headers: authHeaders(true) });
      return await parseJsonResponse<ITestAccuracyOrgsResponse>(res);
    } catch (error) {
      handleApiError(error, 'Failed to load organizations.');
      return { orgs: [] };
    }
  },

  getProjects: async (orgId: string): Promise<ITestAccuracyProjectsResponse> => {
    try {
      const res = await fetch(buildUrl(`${BASE}/projects/`, { org_id: orgId }), {
        headers: authHeaders(true),
      });
      return await parseJsonResponse<ITestAccuracyProjectsResponse>(res);
    } catch (error) {
      handleApiError(error, 'Failed to load projects.');
      return { projects: [] };
    }
  },

  streamDaily: async (
    params: ITestAccuracyDailyParams,
    handlers: ITestAccuracyStreamHandlers,
    signal?: AbortSignal
  ): Promise<void> => {
    const query: Record<string, string> = { org_id: params.org_id };
    if (params.project_id) {
      query.project_id = params.project_id;
    }
    if (params.test_type) {
      query.test_type = params.test_type;
    }
    if (params.source) {
      query.source = params.source;
    }
    if (params.from_date) {
      query.from_date = params.from_date;
    }
    if (params.to_date) {
      query.to_date = params.to_date;
    }
    if (params.section) {
      query.section = params.section;
    }

    const res = await fetch(buildUrl(`${BASE}/daily/`, query), {
      headers: authHeaders(),
      signal,
    });

    if (handleAuthFailureIfNeeded(res)) {
      throw new Error('Session expired. Please login again.');
    }

    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(data.error || res.statusText || 'Failed to load metrics');
    }

    if (!res.body) {
      throw new Error('Streaming is not supported in this browser.');
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const handleLine = (line: string) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return;
      }
      dispatchStreamEvent(JSON.parse(trimmed) as TestAccuracyStreamEvent, handlers);
    };

    let streamDone = false;
    while (!streamDone) {
      const { value, done } = await reader.read();
      if (done) {
        streamDone = true;
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      let newlineIndex = buffer.indexOf('\n');
      while (newlineIndex !== -1) {
        const line = buffer.slice(0, newlineIndex);
        buffer = buffer.slice(newlineIndex + 1);
        handleLine(line);
        newlineIndex = buffer.indexOf('\n');
      }
    }

    if (buffer.trim()) {
      handleLine(buffer);
    }
  },
};
